import { useCallback, useRef } from 'react';

interface TableKeyboardNavigationOptions {
  /**
   * Whether keyboard navigation is enabled for this element
   */
  enabled?: boolean;
  /**
   * Custom handler for arrow key events
   * Note: Left/Right arrows are not handled by default to allow normal text editing
   */
  onArrowKey?: (direction: 'up' | 'down' | 'left' | 'right', event: KeyboardEvent) => boolean;
}

/**
 * Hook for implementing keyboard navigation in table-like structures
 *
 * Navigation behavior:
 * - Up/Down arrows: Move between rows (same column)
 * - Tab/Shift+Tab: Move between columns (same row)
 * - Left/Right arrows: NOT handled (allows normal text editing)
 *
 * Similar to the Knockout arrowKeyNavigation binding handler
 */
export function useTableKeyboardNavigation<T extends HTMLElement = HTMLElement>(
  options: TableKeyboardNavigationOptions = {}
) {
  const { enabled = true, onArrowKey } = options;
  const elementRef = useRef<T>(null);

  const getSibling = useCallback((element: HTMLElement, forward: boolean): HTMLElement | null => {
    return forward ? (element.nextElementSibling as HTMLElement) : (element.previousElementSibling as HTMLElement);
  }, []);

  const getTableCell = useCallback((event: KeyboardEvent): HTMLTableCellElement | null => {
    const target = event.currentTarget as HTMLElement;
    const td = target.closest('td');
    return td instanceof HTMLTableCellElement ? td : null;
  }, []);

  const findFocusableInColumns = useCallback(
    (cell: Element, forward: boolean): HTMLInputElement | HTMLSelectElement | null => {
      let currentCell = cell;
      while (currentCell instanceof HTMLTableCellElement) {
        if (currentCell.style.display !== 'none') {
          // Look for Material-UI Select components first (they have role="button")
          let focusable = currentCell.querySelector('[role="button"]') as HTMLInputElement | HTMLSelectElement;
          if (!focusable || focusable.disabled) {
            // Look for multi-select and other Material-UI components
            focusable = currentCell.querySelector(
              '.MuiSelect-root, .MuiMultiSelect-root, .MuiAutocomplete-root, .MuiChip-root'
            ) as HTMLInputElement | HTMLSelectElement;
          }
          if (!focusable || focusable.disabled) {
            // Look for any focusable element with tabindex
            focusable = currentCell.querySelector('[tabindex]:not([tabindex="-1"])') as
              HTMLInputElement | HTMLSelectElement;
          }
          if (!focusable || focusable.disabled) {
            // Fallback to regular input/select elements
            focusable = currentCell.querySelector('input, select') as HTMLInputElement | HTMLSelectElement;
          }
          if (focusable && !focusable.disabled) {
            return focusable;
          }
        }
        const sibling = getSibling(currentCell, forward);
        if (!sibling) break;
        currentCell = sibling;
      }
      return null;
    },
    [getSibling]
  );

  const findFocusableInRows = useCallback(
    (row: Element, columnIndex: number, forward: boolean): HTMLInputElement | HTMLSelectElement | null => {
      let currentRow = row;
      while (currentRow instanceof HTMLTableRowElement) {
        const cell = currentRow.cells[columnIndex];
        if (cell instanceof HTMLTableCellElement) {
          // Look for Material-UI Select components first (they have role="button")
          let focusable = cell.querySelector('[role="button"]') as HTMLInputElement | HTMLSelectElement;
          if (!focusable || focusable.disabled) {
            // Look for multi-select and other Material-UI components
            focusable = cell.querySelector(
              '.MuiSelect-root, .MuiMultiSelect-root, .MuiAutocomplete-root, .MuiChip-root'
            ) as HTMLInputElement | HTMLSelectElement;
          }
          if (!focusable || focusable.disabled) {
            // Look for any focusable element with tabindex
            focusable = cell.querySelector('[tabindex]:not([tabindex="-1"])') as HTMLInputElement | HTMLSelectElement;
          }
          if (!focusable || focusable.disabled) {
            // Fallback to regular input/select elements
            focusable = cell.querySelector('input, select') as HTMLInputElement | HTMLSelectElement;
          }
          if (focusable && !focusable.disabled) {
            return focusable;
          }
        }
        const sibling = getSibling(currentRow, forward);
        if (!sibling) break;
        currentRow = sibling;
      }
      return null;
    },
    [getSibling]
  );

  const findNextFocusable = useCallback(
    (td: HTMLTableCellElement, forward: boolean): HTMLInputElement | HTMLSelectElement | null => {
      const col = getSibling(td, forward);
      if (col) {
        const focusable = findFocusableInColumns(col, forward);
        if (focusable) {
          return focusable;
        }
      }

      // Try next/previous row
      let nextRow = getSibling(td.parentElement as HTMLElement, forward);
      while (nextRow && nextRow.style.display === 'none') {
        nextRow = getSibling(nextRow, forward);
      }

      if (!nextRow) {
        return null;
      }

      const colInNextRow = forward ? nextRow.firstElementChild : nextRow.lastElementChild;
      if (colInNextRow) {
        return findFocusableInColumns(colInNextRow, forward);
      }

      return null;
    },
    [getSibling, findFocusableInColumns]
  );

  const focusColumnSibling = useCallback(
    (event: KeyboardEvent, forward: boolean): boolean => {
      const td = getTableCell(event);
      if (td) {
        const elem = findNextFocusable(td, forward);
        if (elem) {
          // Remove focus from current element first
          if (document.activeElement && document.activeElement !== elem) {
            (document.activeElement as HTMLElement).blur();
          }

          // For Material-UI Select components, focus the actual select element
          let focusTarget = elem as HTMLElement;
          if (elem.closest('[role="button"]')) {
            const selectElement = elem.closest('[role="button"]') as HTMLElement;
            if (selectElement) {
              focusTarget = selectElement;
            }
          } else if (elem.closest('.MuiSelect-root, .MuiMultiSelect-root, .MuiAutocomplete-root')) {
            const muiElement = elem.closest(
              '.MuiSelect-root, .MuiMultiSelect-root, .MuiAutocomplete-root'
            ) as HTMLElement;
            if (muiElement) {
              focusTarget = muiElement;
            }
          }

          // Apply focus to target element
          focusTarget.focus();

          // FIXME: setTimeout(0) for focus management — see docs/guide/effects-and-async-state.md
          // Force focus styling to show properly, especially for Material-UI components
          setTimeout(() => {
            focusTarget.focus();
            // Trigger a re-render of focus styles
            focusTarget.style.outline = 'none';
            focusTarget.offsetHeight; // Force reflow
            focusTarget.style.outline = '';
          }, 0);

          return true; // Successfully handled
        }
      }
      return false; // Not handled
    },
    [getTableCell, findNextFocusable]
  );

  const focusRowSibling = useCallback(
    (event: KeyboardEvent, forward: boolean): boolean => {
      const td = getTableCell(event);
      if (td) {
        const index = td.cellIndex;
        const row = getSibling(td.parentElement as HTMLElement, forward);
        if (row) {
          const focusable = findFocusableInRows(row, index, forward);
          if (focusable) {
            // Remove focus from current element first
            if (document.activeElement && document.activeElement !== focusable) {
              (document.activeElement as HTMLElement).blur();
            }

            // For Material-UI Select components, focus the actual select element
            let focusTarget = focusable as HTMLElement;
            if (focusable.closest('[role="button"]')) {
              const selectElement = focusable.closest('[role="button"]') as HTMLElement;
              if (selectElement) {
                focusTarget = selectElement;
              }
            } else if (focusable.closest('.MuiSelect-root, .MuiMultiSelect-root, .MuiAutocomplete-root')) {
              const muiElement = focusable.closest(
                '.MuiSelect-root, .MuiMultiSelect-root, .MuiAutocomplete-root'
              ) as HTMLElement;
              if (muiElement) {
                focusTarget = muiElement;
              }
            }

            // Apply focus to target element
            focusTarget.focus();

            // FIXME: setTimeout(0) for focus management — see docs/guide/effects-and-async-state.md
            // Force focus styling to show properly, especially for Material-UI components
            setTimeout(() => {
              focusTarget.focus();
              // Trigger a re-render of focus styles
              focusTarget.style.outline = 'none';
              focusTarget.offsetHeight; // Force reflow
              focusTarget.style.outline = '';
            }, 0);

            return true; // Successfully handled
          }
        }
      }
      return false; // Not handled
    },
    [getTableCell, getSibling, findFocusableInRows]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Check if we're inside an open Select dropdown
      const isSelectOpen =
        event.target &&
        ((event.target as HTMLElement).closest('.MuiSelect-select') ||
          (event.target as HTMLElement).closest('.MuiMenu-root') ||
          (event.target as HTMLElement).closest('[role="listbox"]'));

      // If we're inside an open Select, don't interfere with its navigation
      if (isSelectOpen) {
        return;
      }

      // Allow custom handler to override default behavior
      if (onArrowKey) {
        switch (event.key) {
          case 'ArrowUp':
            if (onArrowKey('up', event)) {
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            break;
          case 'ArrowDown':
            if (onArrowKey('down', event)) {
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            break;
          case 'ArrowLeft':
            if (onArrowKey('left', event)) {
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            break;
          case 'ArrowRight':
            if (onArrowKey('right', event)) {
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            break;
        }
      }

      switch (event.key) {
        case 'Tab':
          if (event.shiftKey) {
            if (focusColumnSibling(event, false)) {
              event.preventDefault();
              event.stopPropagation();
            }
          } else {
            if (focusColumnSibling(event, true)) {
              event.preventDefault();
              event.stopPropagation();
            }
          }
          break;
        case 'ArrowUp':
          if (focusRowSibling(event, false)) {
            event.preventDefault();
            event.stopPropagation();
          }
          break;
        case 'ArrowDown':
          if (focusRowSibling(event, true)) {
            event.preventDefault();
            event.stopPropagation();
          }
          break;
        case 'ArrowLeft':
        case 'ArrowRight':
          // Don't handle left/right arrows to allow normal text editing
          // Users can use Tab/Shift+Tab for column navigation
          break;
        default:
          break;
      }
    },
    [enabled, onArrowKey, focusColumnSibling, focusRowSibling]
  );

  return {
    elementRef,
    handleKeyDown,
  };
}
